import React from 'react'
import { useField, useFormikContext } from 'formik'
import { PayoutState as BasePayoutState, EngagementId } from 'state/tasks'
import { STEP_TYPE_ENUM, EngagementType } from 'services/api/constants'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { getEngagementTranslation } from 'components/utils/translationsMap'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import CheckboxGroup, { OptionState } from 'particles/Checkbox/Group'
import { SelectedEngagementConfig } from './PayoutEditor'

interface EngagementMetricsInputProps {
    name: string
    engagementTypes?: EngagementType[]
    stepType: STEP_TYPE_ENUM
    isReadOnly?: boolean
    identifier?: string
}

interface PayoutState extends BasePayoutState {
    engagementMetrics: SelectedEngagementConfig[]
}

// FIXME: selectedEngagementValues value should follow {label: '', value: ''} instead of {label: '', value: {value: ''}}
const EngagementMetricsInput: React.FC<EngagementMetricsInputProps> = ({
    name,
    engagementTypes,
    stepType,
    isReadOnly = false,
    identifier,
}) => {
    const [t] = useTranslation()
    const [payoutsField] = useField('payouts')
    const [, meta] = useField(name)
    const { setFieldValue } = useFormikContext()
    const hasError = Boolean(meta.touched && meta.error)
    const selectedOptions = payoutsField.value.engagementMetrics.map((option: SelectedEngagementConfig) => option.value)

    const options = engagementTypes
        ? engagementTypes.reduce((acc: OptionState<string>[], currentType: EngagementType) => {
              if (
                  _.isNumber(payoutsField?.value?.[currentType.id]?.cost) &&
                  _.isNumber(payoutsField?.value?.[currentType.id]?.min) &&
                  _.isNumber(payoutsField?.value?.[currentType.id]?.max)
              ) {
                  // For support backward compatibility, still show deprecated metrics if campaign has save deprecated metric before
                  const key =
                      (stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST ||
                          stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST) &&
                      currentType.id === EngagementId.VIEW
                          ? currentType.id + '_video'
                          : currentType.id
                  const label = getEngagementTranslation[key as EngagementId](t)
                  return [
                      ...acc,
                      {
                          value: currentType.id,
                          label: label,
                      },
                  ]
              } else {
                  // only FB task has view_video metric, IG task's view_video metric is deprecated
                  const key =
                      stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST &&
                      currentType.id === EngagementId.VIEW
                          ? currentType.id + '_video'
                          : currentType.id
                  const label = getEngagementTranslation[key as EngagementId](t)
                  return currentType.deprecated === false
                      ? [
                            ...acc,
                            {
                                value: currentType.id,
                                label: label,
                            },
                        ]
                      : acc
              }
          }, [])
        : []

    const payoutsFieldValue = payoutsField.value as unknown as PayoutState
    const handleChecked = (option: string | number): void => {
        const selectedEngagementValues = [...payoutsFieldValue.engagementMetrics]
        const existingEngagementValue = selectedEngagementValues.findIndex(
            engagementValue => engagementValue.value === option.toString(),
        )
        if (existingEngagementValue > -1) {
            selectedEngagementValues.splice(existingEngagementValue, 1)
        } else {
            selectedEngagementValues.push({ value: option.toString() })
        }
        let payoutsUpdateFields = {
            ...payoutsFieldValue,
            engagementMetrics: selectedEngagementValues,
        }
        engagementTypes &&
            engagementTypes.map(engagementType => {
                if (selectedEngagementValues.find(engagementValue => engagementValue.value === engagementType.id)) {
                    payoutsUpdateFields = {
                        ...payoutsUpdateFields,
                        [engagementType.id]: {
                            cost: 0,
                            min: 0,
                            max: 0,
                        },
                    }
                } else {
                    payoutsUpdateFields = {
                        ...payoutsUpdateFields,
                        [engagementType.id]: {
                            cost: '',
                            min: '',
                            max: '',
                        },
                    }
                }
            })

        setFieldValue('payouts' as never, payoutsUpdateFields)
    }

    return (
        <CampaignEditorInputFieldContainer>
            <CheckboxGroup
                title={t('campaignEditor.createTask.selectEngagementMetricsSubheader')}
                subtitle={t('campaignEditor.createTask.selectEngagementMetricSubtext')}
                name={name}
                options={options}
                selectedOptions={selectedOptions}
                onClick={handleChecked}
                isReadOnly={isReadOnly}
                orientation="vertical"
                isRequired
                size="s"
                errorMessage={(hasError && meta.error) || ''}
                identifier={identifier}
            />
        </CampaignEditorInputFieldContainer>
    )
}

export default EngagementMetricsInput
